/**
 * The arithmetic behind the dispatch board, kept out of the component so it
 * can be tested without a browser.
 *
 * Dates here are `YYYY-MM-DD` strings in the *business's* timezone, never the
 * laptop's: an office in Adelaide with a laptop set to Sydney must still see
 * Tuesday's jobs on Tuesday. The server does the one conversion that matters —
 * a local day and minute into an instant — so nothing here builds a UTC time.
 */

export const DAY_START = 7;
export const DAY_END = 17;
export const SLOT_MINUTES = 30;
export const SLOTS = ((DAY_END - DAY_START) * 60) / SLOT_MINUTES;

/** Calendar arithmetic on a date string, safe from the laptop's timezone. */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/** The Monday on or before `day` — weeks on the board start on Monday. */
export function mondayOf(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addDays(day, -((weekday + 6) % 7));
}

/** Today, where the business is. */
export function todayIn(timezone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(now);
}

/** The local date an instant falls on. */
export function localDate(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(new Date(iso));
}

/** Minutes after local midnight. */
export function localMinutes(iso: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0) % 24;
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return h * 60 + m;
}

/** Where on the day's grid a pointer is: the half-hour slot it is over. */
export function slotAt(x: number, left: number, width: number): number {
  if (width <= 0) return 0;
  const slot = Math.floor(((x - left) / width) * SLOTS);
  return Math.min(SLOTS - 1, Math.max(0, slot));
}

export function minuteOfSlot(slot: number): number {
  return DAY_START * 60 + slot * SLOT_MINUTES;
}

export function hhmm(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

/** How many half-hour slots a job covers when it is dropped: its current
 *  booking, else its quoted hours, else an hour — the server's rule too. */
export function slotsFor(job: {
  scheduled_start: string | null;
  scheduled_end: string | null;
  quoted_hours: number | null;
}): number {
  if (job.scheduled_start && job.scheduled_end) {
    const mins = (new Date(job.scheduled_end).getTime() - new Date(job.scheduled_start).getTime()) / 60_000;
    if (mins > 0) return Math.max(1, Math.round(mins / SLOT_MINUTES));
  }
  if (job.quoted_hours) return Math.max(1, Math.ceil((job.quoted_hours * 60) / SLOT_MINUTES));
  return 2;
}

/** "Mon 21 Sep" for a date string. */
export function dayLabel(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}
